import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, CardContent, Typography, Grid, Box, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
    Alert, CircularProgress, Breadcrumbs, Link, Avatar, IconButton,
    Tooltip, InputAdornment, Dialog, DialogTitle, DialogContent,
    DialogActions, Skeleton,
    Chip
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PreviewIcon from '@mui/icons-material/Preview';
import SportsIcon from '@mui/icons-material/Sports';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import InfoIcon from '@mui/icons-material/Info';
import RestoreIcon from '@mui/icons-material/Restore';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

export const SquadreEditPage = () => {
    const { teamId } = useParams();
    const navigate = useNavigate();
    const notify = useNotify();

    // Stati del form
    const [formData, setFormData] = React.useState({
        teamNome: '',
        legaId: '',
        sportId: '',
        foto: '',
        ordinamento: '',
        note: '',
        flVisibile: true,
        flAttivo: true
    });

    // Stati di controllo
    const [loading, setLoading] = React.useState(true);
    const [loadingTeam, setLoadingTeam] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [touched, setTouched] = React.useState({});

    // Gestione modifiche
    const [originalData, setOriginalData] = React.useState({});
    const [hasChanges, setHasChanges] = React.useState(false);
    const [showExitDialog, setShowExitDialog] = React.useState(false);
    const [pendingNavigation, setPendingNavigation] = React.useState(null);

    // Dati squadra originali
    const [teamData, setTeamData] = React.useState(null);
    const [teamError, setTeamError] = React.useState(null);

    // Dati per dropdown
    const [leghe, setLeghe] = React.useState([]);
    const [sports, setSports] = React.useState([]);
    const [loadingLeghe, setLoadingLeghe] = React.useState(true);
    const [loadingSports, setLoadingSports] = React.useState(true);

    // Preview foto
    const [fotoPreview, setFotoPreview] = React.useState('');
    const [fotoError, setFotoError] = React.useState(false);

    // Carica i dettagli della squadra
    const loadTeamDetails = async () => {
        try {
            setLoadingTeam(true);
            setTeamError(null);
            const jwt = getJwt();

            const response = await axios.get(
                `${host}/v-team`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        teamId: parseInt(teamId),
                        $limit: 1
                    }
                }
            );

            const data = response.data;
            if (data.data && data.data.length > 0) {
                const team = data.data[0];
                setTeamData(team);

                // Popola il form con i dati esistenti
                const formDataFromTeam = {
                    teamNome: team.teamNome || '',
                    legaId: team.legaId || '',
                    sportId: team.sportId || '',
                    foto: team.foto || '',
                    ordinamento: team.ordinamento || '',
                    note: team.note || '',
                    flVisibile: team.flVisibile !== undefined ? team.flVisibile : true,
                    flAttivo: team.flAttivo !== undefined ? team.flAttivo : true
                };

                console.log("formDataFromTeam : ", formDataFromTeam);


                setFormData(formDataFromTeam);
                setOriginalData(formDataFromTeam);

                // Imposta preview foto se presente
                if (team.foto) {
                    setFotoPreview(team.foto);
                }

            } else {
                setTeamError("Squadra non trovata");
            }

        } catch (error) {
            console.error("Errore nel recupero dettagli squadra:", error);
            setTeamError("Errore nel caricamento dei dettagli della squadra");
            notify("Errore nel caricamento della squadra", { type: 'error' });
        } finally {
            setLoadingTeam(false);
        }
    };

    // Carica leghe
    const loadLeghe = async () => {
        try {
            setLoadingLeghe(true);
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-lega`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    flVisibile: 1,
                    flAttivo: 1,
                    $sort: { nome: 1 }
                }
            });
            setLeghe(response.data.data || []);
        } catch (error) {
            console.error("Errore nel caricamento leghe:", error);
            notify("Errore nel caricamento leghe", { type: 'error' });
        } finally {
            setLoadingLeghe(false);
        }
    };

    // Carica sport
    const loadSports = async () => {
        try {
            setLoadingSports(true);
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-sport`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    $sort: { nome: 1 }
                }
            });
            setSports(response.data.data || []);
        } catch (error) {
            console.error("Errore nel caricamento sport:", error);
            notify("Errore nel caricamento sport", { type: 'error' });
        } finally {
            setLoadingSports(false);
        }
    };

    // Funzione helper per calcolare solo i campi modificati
    const getChangedFields = (currentData, originalData) => {
        const changes = {};
        const fieldMappings = {
            teamNome: 'nome',
            legaId: 'legaId',
            sportId: 'sportId',
            foto: 'foto',
            ordinamento: 'ordinamento',
            note: 'note',
            flVisibile: 'flVisibile',
            flAttivo: 'flAttivo'
        };

        Object.keys(fieldMappings).forEach(formField => {
            const currentValue = currentData[formField];
            const originalValue = originalData[formField];
            const backendField = fieldMappings[formField];

            // Normalizza i valori per il confronto
            let normalizedCurrent = currentValue;
            let normalizedOriginal = originalValue;

            // Gestione campi specifici
            switch (formField) {
                case 'teamNome':
                    normalizedCurrent = (currentValue || '').trim();
                    normalizedOriginal = (originalValue || '').trim();
                    break;

                case 'legaId':
                case 'sportId':
                    normalizedCurrent = currentValue ? parseInt(currentValue) : null;
                    normalizedOriginal = originalValue ? parseInt(originalValue) : null;
                    break;

                case 'foto':
                case 'note':
                    normalizedCurrent = (currentValue || '').trim() || null;
                    normalizedOriginal = (originalValue || '').trim() || null;
                    break;

                case 'ordinamento':
                    normalizedCurrent = currentValue ? parseInt(currentValue) : null;
                    normalizedOriginal = originalValue ? parseInt(originalValue) : null;
                    break;

                case 'flVisibile':
                case 'flAttivo':
                    normalizedCurrent = Boolean(currentValue);
                    normalizedOriginal = Boolean(originalValue);
                    break;
            }

            // Confronta i valori normalizzati
            if (normalizedCurrent !== normalizedOriginal) {
                changes[backendField] = normalizedCurrent;
            }
        });

        return changes;
    };


    // Validazione campi (identica al create)
    const validateField = (name, value) => {
        const newErrors = { ...errors };

        switch (name) {
            case 'teamNome':
                if (!value.trim()) {
                    newErrors.teamNome = 'Il nome della squadra è obbligatorio';
                } else if (value.trim().length < 2) {
                    newErrors.teamNome = 'Il nome deve essere di almeno 2 caratteri';
                } else if (value.trim().length > 100) {
                    newErrors.teamNome = 'Il nome non può superare i 100 caratteri';
                } else {
                    delete newErrors.teamNome;
                }
                break;

            case 'legaId':
                if (!value) {
                    newErrors.legaId = 'La lega è obbligatoria';
                } else {
                    delete newErrors.legaId;
                }
                break;

            case 'sportId':
                if (!value) {
                    newErrors.sportId = 'Lo sport è obbligatorio';
                } else {
                    delete newErrors.sportId;
                }
                break;

            case 'foto':
                // Nessuna validazione: campo sempre opzionale
                delete newErrors.foto;
                break;

            case 'ordinamento':
                if (value && (isNaN(value) || value < 0)) {
                    newErrors.ordinamento = 'L\'ordinamento deve essere un numero positivo';
                } else {
                    delete newErrors.ordinamento;
                }
                break;

            case 'note':
                if (value && value.length > 500) {
                    newErrors.note = 'Le note non possono superare i 500 caratteri';
                } else {
                    delete newErrors.note;
                }
                break;

            default:
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Controlla se ci sono modifiche
    const checkHasChanges = (newFormData) => {
        const changes = getChangedFields(newFormData, originalData);
        const hasChanges = Object.keys(changes).length > 0;

        // Debug per sviluppo
        if (process.env.NODE_ENV === 'development') {
            if (hasChanges) {
                console.log("🔄 Modifiche rilevate:", changes);
            }
        }

        setHasChanges(hasChanges);
        return hasChanges;
    };

    // Gestisce il cambiamento dei campi
    const handleFieldChange = (name, value) => {
        const newFormData = { ...formData, [name]: value };
        setFormData(newFormData);
        setTouched(prev => ({ ...prev, [name]: true }));

        // Validazione in tempo reale
        validateField(name, value);

        // Controllo modifiche
        checkHasChanges(newFormData);

        // Gestione preview foto
        if (name === 'foto') {
            if (value && value.trim()) {
                setFotoPreview(value.trim());
                setFotoError(false);
            } else {
                setFotoPreview('');
                setFotoError(false);
            }
        }
    };

    // Gestisce errore caricamento foto
    const handleFotoError = () => {
        setFotoError(true);
        // Non aggiungere errori a errors.foto, la preview non deve bloccare il salvataggio
    };

    // Gestisce successo caricamento foto
    const handleFotoLoad = () => {
        setFotoError(false);
        if (errors.foto && errors.foto.includes('Impossibile caricare')) {
            const newErrors = { ...errors };
            delete newErrors.foto;
            setErrors(newErrors);
        }
    };

    // Valida tutto il form
    const validateForm = () => {
        const fieldsToValidate = ['teamNome', 'legaId', 'sportId', 'foto', 'ordinamento', 'note'];
        let isValid = true;

        fieldsToValidate.forEach(field => {
            if (!validateField(field, formData[field])) {
                isValid = false;
            }
            setTouched(prev => ({ ...prev, [field]: true }));
        });

        return isValid;
    };

    // Salva le modifiche
    const handleSave = async () => {
        if (!validateForm()) {
            notify("Correggere gli errori nel form", { type: 'error' });
            return;
        }

        if (!hasChanges) {
            notify("Nessuna modifica da salvare", { type: 'info' });
            return;
        }

        try {
            setSaving(true);
            const jwt = getJwt();

            // 🚀 CALCOLA SOLO I CAMPI MODIFICATI
            const changedFields = getChangedFields(formData, originalData);

            // Verifica che ci siano effettivamente cambiamenti
            if (Object.keys(changedFields).length === 0) {
                notify("Nessuna modifica rilevata", { type: 'info' });
                setSaving(false);
                return;
            }

            console.log("📝 Campi modificati da inviare:", changedFields);
            console.log("🔄 Dati originali:", originalData);
            console.log("✏️ Dati attuali:", formData);

            // Invia solo i campi modificati
            const response = await axios.patch(
                `${host}/tbl-team/${teamId}`,
                changedFields,
                { headers: { 'Authorization': `Bearer ${jwt}` } }
            );

            // Crea messaggio dettagliato sui campi modificati
            const fieldNames = {
                nome: 'Nome',
                legaId: 'Lega',
                sportId: 'Sport',
                foto: 'Foto',
                ordinamento: 'Ordinamento',
                note: 'Note',
                flVisibile: 'Visibilità',
                flAttivo: 'Stato attivo'
            };

            const modifiedFieldsList = Object.keys(changedFields)
                .map(field => fieldNames[field] || field)
                .join(', ');

            notify(
                `Squadra "${formData.teamNome}" aggiornata con successo. Campi modificati: ${modifiedFieldsList}`,
                { type: 'success' }
            );

            // Aggiorna i dati originali per resettare hasChanges
            setOriginalData({ ...formData });
            setHasChanges(false);

            // Torna alla pagina di dettaglio della squadra
            navigate(`/squadre/${teamId}`);

        } catch (error) {
            console.error("Errore nell'aggiornamento squadra:", error);

            if (error.response?.data?.message) {
                notify(`Errore: ${error.response.data.message}`, { type: 'error' });
            } else if (error.response?.status === 409) {
                notify("Una squadra con questo nome esiste già in questa lega", { type: 'error' });
            } else {
                notify("Errore nell'aggiornamento della squadra", { type: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    // Reset ai valori originali
    const handleReset = () => {
        setFormData({ ...originalData });
        setErrors({});
        setTouched({});
        setHasChanges(false);

        // Reset preview foto
        if (originalData.foto) {
            setFotoPreview(originalData.foto);
            setFotoError(false);
        } else {
            setFotoPreview('');
            setFotoError(false);
        }
    };

    // Gestione navigazione con modifiche non salvate
    const handleNavigation = (path) => {
        if (hasChanges) {
            setPendingNavigation(path);
            setShowExitDialog(true);
        } else {
            navigate(path);
        }
    };

    // Conferma uscita senza salvare
    const handleConfirmExit = () => {
        setShowExitDialog(false);
        if (pendingNavigation) {
            navigate(pendingNavigation);
        }
        setPendingNavigation(null);
    };

    // Annulla uscita
    const handleCancelExit = () => {
        setShowExitDialog(false);
        setPendingNavigation(null);
    };

    // Carica dati al mount
    React.useEffect(() => {
        if (teamId) {
            setLoading(true);
            Promise.all([loadTeamDetails(), loadLeghe(), loadSports()]).finally(() => {
                setLoading(false);
            });
        }
    }, [teamId]);

    // Warning per uscita con modifiche non salvate
    React.useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasChanges]);

    // Loading iniziale
    if (loading) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>

                    {/* Breadcrumb skeleton */}
                    <Box sx={{ mb: 3 }}>
                        <Skeleton variant="text" width={300} height={24} />
                    </Box>

                    {/* Header skeleton */}
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Skeleton variant="text" width={250} height={40} />
                            <Skeleton variant="text" width={200} height={20} />
                        </Box>
                        <Skeleton variant="rectangular" width={100} height={36} />
                    </Box>

                    {/* Form skeleton */}
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Skeleton variant="text" width={200} height={32} sx={{ mb: 3 }} />
                                    <Grid container spacing={3}>
                                        {[1, 2, 3, 4, 5, 6].map(i => (
                                            <Grid item xs={12} md={i % 2 === 0 ? 6 : 12} key={i}>
                                                <Skeleton variant="rectangular" height={56} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                    <Skeleton variant="circular" width={80} height={80} sx={{ mx: 'auto', mb: 2 }} />
                                    <Skeleton variant="text" width={150} height={24} />
                                    <Skeleton variant="text" width={100} height={20} />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </motion.div>
            </Box>
        );
    }

    // Error state
    if (teamError) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>

                    {/* Breadcrumb */}
                    <Breadcrumbs sx={{ mb: 3 }}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => handleNavigation('/squadre')}
                            sx={{ textDecoration: 'none' }}
                        >
                            Squadre
                        </Link>
                        <Typography variant="body2" color="text.primary">
                            Errore
                        </Typography>
                    </Breadcrumbs>

                    <Alert
                        severity="error"
                        sx={{ mb: 3 }}
                        action={
                            <Button color="inherit" size="small" onClick={loadTeamDetails}>
                                Riprova
                            </Button>
                        }
                    >
                        {teamError}
                    </Alert>

                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => handleNavigation('/squadre')}
                    >
                        Torna alla lista
                    </Button>
                </motion.div>
            </Box>
        );
    }

    const hasErrors = Object.keys(errors).length > 0;
    const canSave = formData.teamNome.trim() && formData.legaId && formData.sportId && !hasErrors && hasChanges;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >

                {/* Breadcrumb */}
                <Breadcrumbs sx={{ mb: 3 }}>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleNavigation('/squadre')}
                        sx={{ textDecoration: 'none' }}
                    >
                        Squadre
                    </Link>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleNavigation(`/squadre/${teamId}`)}
                        sx={{ textDecoration: 'none' }}
                    >
                        {teamData?.teamNome || `Squadra ${teamId}`}
                    </Link>
                    <Typography variant="body2" color="text.primary">
                        Modifica
                    </Typography>
                </Breadcrumbs>

                {/* Header */}
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            ⚽ Modifica Squadra
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Modifica i dettagli di "{teamData?.teamNome || 'Squadra'}"
                            {hasChanges && (
                                <Chip
                                    label="Modifiche non salvate"
                                    size="small"
                                    color="warning"
                                    sx={{ ml: 1 }}
                                />
                            )}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={loadTeamDetails}
                            disabled={saving}
                        >
                            Ricarica
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => handleNavigation(`/squadre/${teamId}`)}
                        >
                            Indietro
                        </Button>
                    </Box>
                </Box>

                {/* Alert modifiche */}
                {hasChanges && (
                    <Alert
                        severity="info"
                        sx={{ mb: 3 }}
                        action={
                            <Button color="inherit" size="small" onClick={handleReset}>
                                Annulla modifiche
                            </Button>
                        }
                    >
                        Hai delle modifiche non salvate. Ricordati di salvare prima di uscire.
                    </Alert>
                )}

                {/* Form */}
                <Grid container spacing={3}>

                    {/* Colonna principale - Dati squadra */}
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                            <CardContent sx={{ p: 3 }}>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <InfoIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Informazioni Squadra
                                    </Typography>
                                </Box>

                                <Grid container spacing={3}>

                                    {/* Nome squadra */}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Nome Squadra *"
                                            value={formData.teamNome}
                                            onChange={(e) => handleFieldChange('teamNome', e.target.value)}
                                            error={touched.teamNome && !!errors.teamNome}
                                            helperText={touched.teamNome && errors.teamNome}
                                            placeholder="Inserisci il nome della squadra"
                                        />
                                    </Grid>

                                    {/* Lega */}
                                    <Grid item xs={12} md={6}>
                                        <FormControl
                                            fullWidth
                                            error={touched.legaId && !!errors.legaId}
                                        >
                                            <InputLabel>Lega *</InputLabel>
                                            <Select
                                                value={formData.legaId}
                                                label="Lega *"
                                                onChange={(e) => handleFieldChange('legaId', e.target.value)}
                                                disabled={loadingLeghe}
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <EmojiEventsIcon sx={{ color: '#1565c0' }} />
                                                    </InputAdornment>
                                                }
                                            >
                                                {loadingLeghe ? (
                                                    <MenuItem disabled>
                                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                                        Caricamento...
                                                    </MenuItem>
                                                ) : (
                                                    leghe.map(lega => (
                                                        <MenuItem key={lega.id} value={lega.id}>
                                                            {lega.nome}
                                                            {lega.descrizione && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                                    - {lega.descrizione}
                                                                </Typography>
                                                            )}
                                                        </MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                            {touched.legaId && errors.legaId && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                                    {errors.legaId}
                                                </Typography>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    {/* Sport */}
                                    <Grid item xs={12} md={6}>
                                        <FormControl
                                            fullWidth
                                            error={touched.sportId && !!errors.sportId}
                                        >
                                            <InputLabel>Sport *</InputLabel>
                                            <Select
                                                value={formData.sportId}
                                                label="Sport *"
                                                onChange={(e) => handleFieldChange('sportId', e.target.value)}
                                                disabled={loadingSports}
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <SportsIcon sx={{ color: '#e65100' }} />
                                                    </InputAdornment>
                                                }
                                            >
                                                {loadingSports ? (
                                                    <MenuItem disabled>
                                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                                        Caricamento...
                                                    </MenuItem>
                                                ) : (
                                                    sports.map(sport => (
                                                        <MenuItem key={sport.id} value={sport.id}>
                                                            {sport.nome}
                                                            {sport.descrizione && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                                    - {sport.descrizione}
                                                                </Typography>
                                                            )}
                                                        </MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                            {touched.sportId && errors.sportId && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                                    {errors.sportId}
                                                </Typography>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    {/* URL Foto */}
                                    <Grid item xs={12}>
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            Alcuni servizi di hosting immagini (es. Google Drive, Dropbox, ecc.) possono <b>bloccare la visualizzazione dell’anteprima nel form</b> per motivi tecnici (CORS o permessi), anche se l’URL è raggiungibile da browser.<br />
                                            Il link viene comunque salvato e sarà visibile dove supportato. Per una preview sempre funzionante, usa link diretti (es. Imgur, server tuo, ecc.).
                                        </Alert>
                                        <TextField
                                            fullWidth
                                            label="URL Logo/Foto"
                                            value={formData.foto}
                                            onChange={(e) => handleFieldChange('foto', e.target.value)}
                                            error={touched.foto && !!errors.foto}
                                            helperText={touched.foto && errors.foto}
                                            placeholder="https://esempio.com/logo.png"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PhotoCameraIcon />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: formData.foto && (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Anteprima">
                                                            <IconButton size="small">
                                                                <PreviewIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    {/* Ordinamento */}
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Ordinamento"
                                            type="number"
                                            value={formData.ordinamento}
                                            onChange={(e) => handleFieldChange('ordinamento', e.target.value)}
                                            error={touched.ordinamento && !!errors.ordinamento}
                                            helperText={touched.ordinamento && errors.ordinamento || "Numero per ordinare le squadre"}
                                            placeholder="0"
                                            inputProps={{ min: 0, step: 1 }}
                                        />
                                    </Grid>

                                    {/* Note */}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Note"
                                            multiline
                                            rows={3}
                                            value={formData.note}
                                            onChange={(e) => handleFieldChange('note', e.target.value)}
                                            error={touched.note && !!errors.note}
                                            helperText={touched.note && errors.note || `${formData.note.length}/500 caratteri`}
                                            placeholder="Note aggiuntive sulla squadra..."
                                        />
                                    </Grid>

                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Colonna laterale - Preview e controlli */}
                    <Grid item xs={12} md={4}>

                        {/* Preview squadra */}
                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    Anteprima Squadra
                                </Typography>

                                <Avatar
                                    src={fotoPreview && !fotoError ? `${host}/image-proxy-mw?url=${encodeURIComponent(fotoPreview)}` : ''}
                                    alt={formData.teamNome}
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        margin: '0 auto 16px',
                                        bgcolor: 'primary.main',
                                        fontSize: '2rem'
                                    }}
                                    onError={handleFotoError}
                                    onLoad={handleFotoLoad}
                                >
                                    {!fotoPreview || fotoError ?
                                        (formData.teamNome ? formData.teamNome.charAt(0).toUpperCase() : '⚽') :
                                        null
                                    }
                                </Avatar>

                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    {formData.teamNome || 'Nome Squadra'}
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
                                    {formData.legaId && (
                                        <Typography variant="body2" color="primary">
                                            {leghe.find(l => l.id === parseInt(formData.legaId))?.nome || 'Lega'}
                                        </Typography>
                                    )}
                                    {formData.legaId && formData.sportId && (
                                        <Typography variant="body2" color="text.secondary">•</Typography>
                                    )}
                                    {formData.sportId && (
                                        <Typography variant="body2" color="primary">
                                            {sports.find(s => s.id === parseInt(formData.sportId))?.nome || 'Sport'}
                                        </Typography>
                                    )}
                                </Box>

                                {formData.note && (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        {formData.note.length > 100 ? `${formData.note.substring(0, 100)}...` : formData.note}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>

                        {/* Impostazioni */}
                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    Impostazioni
                                </Typography>

                                <Box sx={{ space: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.flVisibile}
                                                onChange={(e) => handleFieldChange('flVisibile', e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Squadra visibile pubblicamente"
                                        sx={{ mb: 1 }}
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.flAttivo}
                                                onChange={(e) => handleFieldChange('flAttivo', e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Squadra attiva"
                                    />
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Errori */}
                        {hasErrors && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Correggere i seguenti errori:
                                </Typography>
                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                    {Object.values(errors).map((error, index) => (
                                        <li key={index}>
                                            <Typography variant="body2">{error}</Typography>
                                        </li>
                                    ))}
                                </ul>
                            </Alert>
                        )}

                        {/* Azioni */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={!canSave || saving}
                                sx={{ py: 1.5 }}
                            >
                                {saving ? 'Salvataggio...' : 'Salva Modifiche'}
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<RestoreIcon />}
                                onClick={handleReset}
                                disabled={saving || !hasChanges}
                            >
                                Ripristina Originale
                            </Button>
                        </Box>
                    </Grid>

                </Grid>

                {/* Footer */}
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        * Campi obbligatori
                        {hasChanges && " • Hai modifiche non salvate"}
                    </Typography>
                </Box>

                {/* Dialog conferma uscita */}
                <Dialog
                    open={showExitDialog}
                    onClose={handleCancelExit}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon color="warning" />
                        Modifiche non salvate
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1">
                            Hai delle modifiche non salvate. Se esci ora, le modifiche andranno perse.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Vuoi davvero uscire senza salvare?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelExit}>
                            Rimani qui
                        </Button>
                        <Button onClick={handleConfirmExit} color="warning" variant="contained">
                            Esci senza salvare
                        </Button>
                    </DialogActions>
                </Dialog>

            </motion.div>
        </Box>
    );
};